import { toast } from '@tamagui/toast/v2';
import { format, isBefore, parseISO } from 'date-fns';
import { Stack, useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import { useEffect } from 'react';
import { Platform } from 'react-native';
import { Text, YStack } from 'tamagui';

import BookingSkeleton from '@/components/BookingSkeleton';
import CheckoutCart from '@/components/CheckoutCart';
import CheckoutEventHeader from '@/components/CheckoutEventHeader';
import CheckoutUserDetails from '@/components/CheckoutUserDetails';
import Section from '@/components/Section';
import PSLinkButton from '@/components/ui/PSLinkButton';
import PSRichText from '@/components/ui/PSRichText';
import PSText from '@/components/ui/PSText';
import PageScrollView from '@/components/ui/PageScrollView';
import { UserProfile } from '@/src/api/authApi';
import type { Booking, BookingStatus } from '@/src/api/bookingsApi';
import { usePollBooking } from '@/src/hooks/usePollBooking';
import { useSessionUser } from '@/src/lib/sessionStore';
import { openURL } from '@/src/utils/linkingUtils';
import { PSHeaderBackButtonOnTransparentHeader } from '@/components/PSHeaderButtons';
import { formatDateTime } from '@/src/utils/dateFormatter';
import PSBox from '@/components/ui/PSBox';
import Footer from '@/components/Footer';
import { StatusBar } from 'expo-status-bar';

/**
 * The booking detail page — the ticket. Where the confirm step lands the user
 * after paying, and where a booking in their list opens.
 *
 * It's a near-copy of the confirm step's receipt (same header, slot line,
 * itemised cart, your-details, terms), with one difference: the SELECTION. On
 * confirm it's live, still being chosen out of the shared cart; here it's FROZEN
 * onto the booking — the slot and the per-tier lines were fixed at purchase and
 * are read straight off `GET /bookings/{id}`, which nests the event and slot so
 * this whole screen is one fetch.
 *
 * ── It is also the WAITING ROOM ─────────────────────────────────────────
 * The user gets here the moment a booking EXISTS, which is before the money has
 * landed: `POST /bookings` only opens a Razorpay order, and the booking is
 * confirmed server-side when the capture webhook arrives. Nothing tells the
 * client that, so `usePollBooking` keeps asking until the booking leaves
 * `pending_payment`, and the skeleton covers the wait. Opening a ticket from the
 * bookings list is the same code path with the poll settling on its first ask.
 *
 * NOTE: loading shows `BookingSkeleton` (the page's shape, pulsing) so the real
 * screen slots in without a jump; error still renders nothing — it gets designed
 * later, like the detail page's error screen. Two failures land there now: the
 * booking couldn't be read, and the poll gave up waiting for the capture. The
 * second is the more interesting one to design.
 */
export default function BookingPage() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const state = usePollBooking(id);
  const user = useSessionUser();

  // The errored screen draws NOTHING yet, so without this the message dies with
  // the request and the user is left on a blank page — most likely after a
  // minute of skeleton, which is the worst version of it. Toast carries it in the
  // meantime, the same way the home screen does. Keyed on the error object, which
  // is minted once when the poll settles, so it fires exactly once.
  useEffect(() => {
    if (state.error) toast.error(state.error.message);
  }, [state.error]);

  // Loading → the skeleton (mirrors the page's shape). The user is guaranteed
  // present on this screen (only reached logged in), but the session type is
  // nullable, so treat "no user yet" as loading too rather than flashing empty.
  if (state.status === 'loading' || user === null) {
    return <BookingSkeleton />;
  }

  // Error is unbuilt for now — render nothing rather than a half receipt.
  if (state.status === 'error') {
    return null;
  }

  return <BookingBody booking={state.booking} user={user} />;
}

/**
 * What the big second line reads, per status. The poll only settles once the
 * booking has LEFT `pending_payment`, so every entry here but that one is
 * reachable — and a cancelled booking must not be handed a screen that says
 * "Confirmed", which is why this is a lookup and not a constant.
 *
 * Both refund states read "Refunded": the difference between a full and a
 * partial refund is an amount, and the amount is in the cart box below.
 */
const STATUS_HEADLINE: Record<BookingStatus, string> = {
  pending_payment: 'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  expired: 'Expired',
  refunded: 'Refunded',
  partially_refunded: 'Refunded',
};

/**
 * The 48px headline's line height, web only.
 *
 * Tamagui's web CSS pins every element to the font config's `true` line height
 * (20px) and only lifts it for sizes that font's `lineHeight` map names —
 * `$veryLarge` isn't one, so both lines land in a 20px box and collide. Native
 * never reads that map; it lets the face's own metrics size the line. These ARE
 * those metrics at 48px (GeneralSans-Semibold 1.354em, Gochi Hand 1.177em), so
 * web lands where native already is and native itself is left untouched.
 */
const HEADLINE_LINE_HEIGHT = {
  body: Platform.select({ web: 65 }),
  hand: Platform.select({ web: 57 }),
};

/**
 * The booking body. Every value it prints comes off the booking — the money
 * already formatted by the server (`display` on each amount), the tier names on
 * the lines, the event and slot nested in — so the only thing it works out is
 * the slot's date/time line.
 */
function BookingBody({
  booking,
  user,
}: {
  booking: Booking;
  user: UserProfile;
}) {
  const { event, slot, tickets } = booking;

  const isEventInPast =
    booking.status === 'confirmed' && isBefore(slot.endDatetime, new Date());

  const hasDiscount = booking.discount.amountInMinorUnits > 0;

  const { support } = event;
  return (
    <YStack flex={1} bg="$color.background">
      <Head>
        <title>Booking · Putting Scene</title>
      </Head>
      {/* Header: blank title + the shared back circle (same PSCircleButton,
          wired both ways for the iOS-26 double-ring fix — see authHeaderLeftOptions).
          Transparent like every screen's, so the root SafeAreaView's dropped top
          edge stays correct. */}
      <Stack.Screen
        options={{
          headerTransparent: true,
          headerTitle: () => <></>,

          headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButtonOnTransparentHeader />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <StatusBar style="dark" />
      <PageScrollView>
        <YStack justifyContent="center" mb={'$space.xxl'} gap={'$space.xs'}>
          <PSText.Title
            color={isEventInPast ? '$color.brandGray' : '$color.brand'}
          >
            {isEventInPast ? 'Past' : STATUS_HEADLINE[booking.status]}
          </PSText.Title>
          <PSText.Title>Booking{isEventInPast ? '' : '!'}</PSText.Title>
        </YStack>
        <YStack gap="$space.xl">
          <CheckoutEventHeader
            // One image on a booking — the receipt shows a single thumbnail.
            imageUrl={event.images[0]?.url ?? ''}
            grayScale={isEventInPast}
            title={event.title}
            slotDisplay={formatDateTime(slot.startDatetime)}
            venueName={event.venue.name}
            googleMapsUrl={event.venue.googleMapsUrl}
          />

          {/* Tickets — one line per booked tier. Nothing is added up here: every
              amount arrives formatted (`lineTotal.display`), and the total is the
              server's own `amountDue`, not a sum of the lines — that's what makes
              it right when a coupon discounted the order. */}
          <CheckoutCart
            headerLabel="Tickets"
            lines={tickets.map((ticket) => ({
              id: ticket.slotTierId,
              name: ticket.ticketTierName,
              quantity: ticket.quantity,
              subtotalDisplay: ticket.lineTotal.display,
            }))}
            totalDisplay={
              hasDiscount ? booking.total.display : booking.total.display
            }
            subtotalDisplay={hasDiscount ? booking.subtotal.display : undefined}
            couponDiscountDisplay={
              hasDiscount ? `-${booking.discount.display}` : undefined
            }
          />

          {/* Your details — the booking contact, guaranteed present (logged in). */}
          <CheckoutUserDetails
            name={user.fullName}
            // Null only for a logged-in user whose profile isn't finished yet —
            // a state this screen shouldn't be reachable in, but isn't gated on
            // yet. See the incomplete-profile TODO in [[session]].
            email={user.email ?? ''}
            mobile={user.mobile}
            socialLink={user.socialMediaLink || undefined}
          />

          {/* Terms */}
          {event.termsAndConditions && (
            <Section headerLabel="Terms &amp; conditions">
              <PSRichText markdown={event.termsAndConditions} />
            </Section>
          )}

          {/* Support — the event's go-to contact. The name is bold, and phone /
              email are inline PSLinkButtons (tel:/mailto:). Email is optional:
              when absent, both the " or {email}" tail and its link are dropped, so
              the sentence still reads cleanly ("…reach out to them on {phone}."). */}
          {support.mobile && (
            <Section headerLabel="Support">
              {/* One flowing paragraph: the link atoms nest inside the copy Text so
                phone/email sit inline (RN renders nested pressable Text inline). */}
              <PSText.Body2>
                <Text fontWeight="$semibold">{support.name}</Text> will be your
                go-to person for any queries related to the event. You can reach
                out to them on{' '}
                <PSLinkButton
                  title={support.mobile}
                  // Strip display formatting (spaces/dashes) for the dialer, keep a
                  // leading "+"; the visible label stays the formatted number.
                  onPress={() =>
                    openURL(`tel:${support.mobile.replace(/[^\d+]/g, '')}`)
                  }
                />
                {support.email ? (
                  <>
                    {' '}
                    or{' '}
                    <PSLinkButton
                      title={support.email}
                      onPress={() => openURL(`mailto:${support.email}`)}
                    />
                  </>
                ) : null}
                .
              </PSText.Body2>
            </Section>
          )}
        </YStack>

        <Footer />
      </PageScrollView>
    </YStack>
  );
}
